/**
 * Maps a failed staff API call to user-facing copy. Status codes follow the
 * Swagger docs for /staff/videos and /staff/playlists.
 *
 * Contexts:
 * - 'prefill'  : POST /staff/videos/prefill (a 400 means the URL is not a YouTube video)
 * - 'youtube'  : create and refresh (the server talks to YouTube)
 * - 'playlist' : import-preview and import (backend rejection text, including an
 *                audience conflict 409, is shown as is)
 * - 'default'  : everything else
 *
 * @param {{ status?: number, serverMessage?: string|null }|null|undefined} error
 * @param {(key: string, opts?: Object) => string} t
 * @param {'prefill'|'youtube'|'playlist'|'default'} [context]
 * @returns {{ message: string, detail: string|null }}
 */
export default function getErrorMessage(error, t, context = 'default') {
  const status = error && typeof error.status === 'number' ? error.status : -1;
  const serverMessage = (error && error.serverMessage) || null;
  const key = (name) => t(`staff_videos.errors.${name}`);
  const withDetail = (message) => ({ message, detail: serverMessage });

  if (status === 0) return { message: key('network'), detail: null };
  if (status === 502) return withDetail(key('youtube_unreachable'));
  if (status === 503) return withDetail(key('youtube_unavailable'));

  if (context === 'playlist') {
    // The backend explains rejections such as auto-generated mixes,
    // Liked/Watch later lists, private playlists and an audience already held
    // by a different playlist (409). Show that text as is.
    if ([400, 403, 404, 409].includes(status) && serverMessage) {
      return { message: serverMessage, detail: null };
    }
    if (status === 404) return withDetail(key('playlist_not_found'));
    if (status === 400) return withDetail(key('invalid_url'));
  }

  if (context === 'prefill' && status === 400) {
    return withDetail(key('invalid_url'));
  }

  if (context === 'prefill' || context === 'youtube') {
    if (status === 404) return withDetail(key('not_found'));
    if (status === 409) return withDetail(key('duplicate'));
  }

  if (status === 403) return withDetail(key('forbidden'));

  return withDetail(key('generic'));
}
